import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isDefined, isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import PQueue from 'p-queue';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { useAppSelector } from '@/store/hooks.ts';
import { useValidateCallOrderMutation } from '@/store/orders/call/api/index.ts';
import { selectCallOrderTile } from '@/store/orders/call/selectors.ts';
import type { CallOrder } from '@/store/orders/call/state.ts';
import type { ValidateOrderResponse } from '@/store/orders/state.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { normalizeEmailsList } from '@/utils/notificationEmails.ts';

export type CallOrderContextContent = {
  tileId: string;
  isChecking: boolean;
  isModifyingField: boolean;
  setIsModifyingField: (isModifying: boolean) => void;
  validateCallOrder: CallOrderValidateFn;
};

type CallOrderValidateFn = (
  updatedProperties: Partial<CallOrder['properties']>,
) => Promise<
  | { data: ValidateOrderResponse; error: undefined }
  | { data: undefined; error: FetchBaseQueryError | SerializedError }
  | undefined
>;

type CallOrderModifyingFieldContextContent = {
  isModifyingField: boolean;
  setIsModifyingField: (isModifying: boolean) => void;
};

const CallOrderTileIdContext = createContext('');
const CallOrderIsCheckingContext = createContext(false);
const CallOrderValidateContext = createContext<CallOrderValidateFn>(async (_updatedProperties) => undefined);
const CallOrderModifyingFieldContext = createContext<CallOrderModifyingFieldContextContent>({
  isModifyingField: false,
  setIsModifyingField: () => {},
});

export const callOrderContext = createContext<CallOrderContextContent>({
  tileId: '',
  isChecking: false,
  validateCallOrder: async (_updatedProperties: Partial<CallOrder['properties']>) => undefined,
  isModifyingField: false,
  setIsModifyingField: () => {},
});

export const CallOrderProvider = (props: { tileId: string; children: ReactNode }) => {
  const [isChecking, setIsChecking] = useState(false);
  const [isModifyingField, setIsModifyingField] = useState(false);

  const { children, tileId } = props;
  const [validateCallOrderMutation] = useValidateCallOrderMutation({
    selectFromResult: () => ({}),
  });
  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));

  const { tile, preferencesNotificationEmails } = useAppSelector((state) => {
    const userPreferences = state.userPreferences;

    if (isNotDefined(userPreferences)) {
      console.error('[CallOrderProvider] User preferences not found in state.');
      return {};
    }

    const tile = selectCallOrderTile(state, tileId);

    if (isNotDefined(tile)) {
      console.error(`[CallOrderProvider] Tile with id ${tileId} not found in state.`);
      return {};
    }

    if (tile.type !== 'CallOrder') {
      console.error(`[CallOrderProvider] Tile with id ${tileId} is not a CallOrder.`);
      return {};
    }

    return {
      tile,
      preferencesNotificationEmails: userPreferences.splitNotifications
        ? userPreferences.splitNotificationsEmailsOrders
        : userPreferences.emails,
    };
  }, deepEqual);

  // Queue with concurrency=1 to ensure sequential execution
  // Only one handleValidateCallOrder request can run at a time
  const queue = useMemo(() => new PQueue({ concurrency: 1 }), []);

  const handleValidateCallOrder = useCallback(
    async (updatedProperties: Partial<CallOrder['properties']>) => {
      return queue.add(async () => {
        if (isNotDefined(tile)) {
          console.error(`[handleValidateCallOrder] Tile with id ${tileId} not found in state.`);
          return null;
        }

        const currency1 = updatedProperties.currencyPair?.currency1 ?? tile.properties.currencyPair?.currency1;
        const currency2 = updatedProperties.currencyPair?.currency2 ?? tile.properties.currencyPair?.currency2;

        if (isNotDefined(clientId)) {
          console.error('[handleValidateCallOrder] client id is not defined');
          return null;
        }

        if (isNotDefined(currency1) || isNotDefined(currency2)) {
          console.error(`[handleValidateCallOrder] Invalid currency pair: ${currency1}/${currency2}`);
          return null;
        }

        const updatedCallOrder = {
          ...tile,
          properties: {
            ...tile.properties,
            ...updatedProperties,
          },
        };

        const patchedKeys = Object.keys(updatedProperties);
        const keysWhichShouldMakeLimitPriceReset = ['currencyPair'];
        const shouldResetLimitPrice = keysWhichShouldMakeLimitPriceReset.some((key) => patchedKeys.includes(key));

        const notificationEmails = normalizeEmailsList(
          updatedCallOrder.properties.notificationEmails ?? preferencesNotificationEmails ?? [],
        );

        const checkTimer = setTimeout(() => {
          setIsChecking(true);
        }, 500);

        const data = await validateCallOrderMutation({
          tileId,
          currency1,
          currency2,
          clientId,
          limitPrice: shouldResetLimitPrice ? undefined : updatedCallOrder.properties.limitPrice,
          expiry: updatedCallOrder.properties.expiry,
          notificationEmails: {
            isOverridenEmails: isDefined(updatedProperties.notificationEmails),
            emails: notificationEmails,
          },
          clockOffset: updatedCallOrder.properties.clockOffset,
        });

        clearTimeout(checkTimer);
        setIsChecking(false);
        return data;
      });
    },
    [tile, preferencesNotificationEmails, tileId, validateCallOrderMutation, clientId, queue],
  );

  const validateCallOrder = useCallback<CallOrderValidateFn>(
    async (updatedProperties) => {
      const result = await handleValidateCallOrder(updatedProperties);

      if (isNotDefined(result)) {
        console.error('[validate] Validation result is undefined');
        return undefined;
      }

      if ('data' in result && result.data !== undefined) {
        return { data: result.data, error: undefined };
      }

      if ('error' in result && result.error !== undefined) {
        return { data: undefined, error: result.error };
      }

      return undefined;
    },
    [handleValidateCallOrder],
  );

  const modifyingFieldContent = useMemo(
    () => ({
      isModifyingField,
      setIsModifyingField,
    }),
    [isModifyingField],
  );

  const content = useMemo(
    () => ({
      tileId,
      isChecking,
      validateCallOrder,
      ...modifyingFieldContent,
    }),
    [isChecking, modifyingFieldContent, tileId, validateCallOrder],
  );

  return (
    <CallOrderTileIdContext.Provider value={tileId}>
      <CallOrderIsCheckingContext.Provider value={isChecking}>
        <CallOrderValidateContext.Provider value={validateCallOrder}>
          <CallOrderModifyingFieldContext.Provider value={modifyingFieldContent}>
            <callOrderContext.Provider value={content}>{children}</callOrderContext.Provider>
          </CallOrderModifyingFieldContext.Provider>
        </CallOrderValidateContext.Provider>
      </CallOrderIsCheckingContext.Provider>
    </CallOrderTileIdContext.Provider>
  );
};

export function useCallOrderTileId() {
  return useContext(CallOrderTileIdContext);
}

export function useCallOrderIsChecking() {
  return useContext(CallOrderIsCheckingContext);
}

export function useValidateCallOrder() {
  return useContext(CallOrderValidateContext);
}

export function useCallOrderIsModifyingField() {
  return useContext(CallOrderModifyingFieldContext).isModifyingField;
}

export function useCallOrderSetIsModifyingField() {
  return useContext(CallOrderModifyingFieldContext).setIsModifyingField;
}
